import type { CSSProperties } from 'react';
import {
  coverShade,
  kitFontCssFamily,
  ReleaseThemeId,
  KitFontId,
  themeColours,
  themeType,
} from 'fym-shared/src/music24/releaseThemes';
import { LyricSampleStyle, themeSampleFiles } from 'fym-shared/src/music24/releaseKitLookSamples';

/** How the still places the cover (the image styles of design q3c). */
export type ThemeStillLayout = 'card' | 'ground' | 'type';

/**
 * The type of image styles in which the title grows (design `GROW`): with no cover card the
 * title takes more room.
 */
const GROW: Readonly<Record<ReleaseThemeId, number>> = {
  clean: 1.35,
  hype: 1,
  dreamy: 1.2,
  dark: 1.3,
  warm: 1.3,
  retro: 1.2,
  neon: 1.25,
  handmade: 1.15,
};

export interface ThemeStillInput {
  themeId: ReleaseThemeId;
  fontOverride: KitFontId | null;
  /** The 5 cover colours of the song. An empty palette gives the fallback colours. */
  palette: readonly string[];
  /** The absolute url of the artwork, or undefined. */
  artworkUrl: string | undefined;
  title: string;
  layout: ThemeStillLayout;
}

/**
 * The CSS variables of a theme still (design q, `vars` of the logic class): the theme colours
 * from the cover palette (`themeColours`, the same code as the renderer), three faded cover
 * shades for the Retro bands, the cover image, and the display type of the look (`themeType`).
 * `--k` shrinks a long title so it stays in the frame. Pure.
 */
export function themeStillVars(input: ThemeStillInput): CSSProperties {
  const { themeId, fontOverride, palette, artworkUrl, title, layout } = input;
  const colours = themeColours(themeId, palette);
  const type = themeType({ themeId, fontOverride });
  const grow = layout === 'card' ? 1 : GROW[themeId];
  const length = Math.max(1, Array.from(title).length);
  const vars: Record<string, string | number> = {
    '--g': colours.background,
    '--s': colours.surface,
    '--t': colours.text,
    '--a': colours.accent,
    '--oa': colours.onAccent,
    '--f1': coverShade(palette, { from: 'mid', L: 0.42, Cx: 0.8 }),
    '--f2': coverShade(palette, { from: 'vivid', L: 0.68, Cx: 0.7 }),
    '--f3': coverShade(palette, { from: 'light', L: 0.82, Cx: 0.6 }),
    '--cv': artworkUrl === undefined ? 'none' : `url("${artworkUrl.replace(/"/g, '%22')}")`,
    '--df': kitFontCssFamily(type.fonts.display),
    '--dw': type.display.weight,
    '--ds': type.display.italic ? 'italic' : 'normal',
    '--tf': kitFontCssFamily(type.fonts.text),
    '--k': Math.min(1, 6 / length) * grow,
    '--fz': type.display.scale,
    '--dc': type.display.case,
    '--dl': `${type.display.tracking}em`,
  };
  return vars as CSSProperties;
}

function publicUrl(file: string): string {
  return `${process.env.PUBLIC_URL ?? ''}/${file}`;
}

/** The sample loop and its poster of a theme: static files that `pnpm look:render-samples` makes. */
export function themeSampleUrls(themeId: ReleaseThemeId): { video: string; poster: string } {
  const files = themeSampleFiles(themeId);
  return { video: publicUrl(files.video), poster: publicUrl(files.poster) };
}

/**
 * The lyric card sample of a theme in a lyric card style: a still of the real renderer with a
 * chorus line of "Forests" (`pnpm look:render-samples` makes it).
 */
export function lyricSampleUrl(themeId: ReleaseThemeId, style: LyricSampleStyle): string {
  return publicUrl(themeSampleFiles(themeId).lyricCards[style]);
}
